"use client";

import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { X } from "lucide-react";

import { Button } from "../../inputs/button";
import { PropertyTrigger } from "./property-trigger";
import { Calendar } from "../../primitives/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../primitives/popover";
import { cn } from "../../utils";

interface DateSelectorPopoverProps {
  value: number | null;
  selected: Date | undefined;
  calendarSelected: Date | undefined;
  disabled: boolean;
  icon: LucideIcon;
  placeholder: string;
  formattedValue: string | null;
  removeLabel: string;
  onCalendarSelect: (day: Date) => void;
  onRemove: () => void;
  onOpen?: () => void;
  closeOnCalendarSelect?: boolean;
  renderFooter?: (removeButton: React.ReactNode, close: () => void) => React.ReactNode;
}

export function DateSelectorPopover({
  value,
  selected,
  calendarSelected,
  disabled,
  icon: Icon,
  placeholder,
  formattedValue,
  removeLabel,
  onCalendarSelect,
  onRemove,
  onOpen,
  closeOnCalendarSelect = false,
  renderFooter,
}: DateSelectorPopoverProps) {
  const [open, setOpen] = React.useState(false);

  const removeButton =
    value == null ? null : (
      <Button
        variant="ghost"
        size="sm"
        className="h-7 justify-start gap-2 px-2 text-xs text-muted-foreground"
        onClick={() => {
          onRemove();
          setOpen(false);
        }}
      >
        <X className="h-3 w-3" />
        {removeLabel}
      </Button>
    );

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) onOpen?.();
      }}
    >
      <PopoverTrigger asChild>
        <PropertyTrigger
          disabled={disabled}
          className={cn(value == null && "text-muted-foreground")}
        >
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-[13px]">{formattedValue ?? placeholder}</span>
        </PropertyTrigger>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={calendarSelected}
          onSelect={(day) => {
            if (!day) {
              if (closeOnCalendarSelect) setOpen(false);
              return;
            }
            onCalendarSelect(day);
            if (closeOnCalendarSelect) setOpen(false);
          }}
          {...(selected ? { defaultMonth: selected } : {})}
          autoFocus
        />
        {renderFooter ? (
          renderFooter(removeButton, () => setOpen(false))
        ) : removeButton ? (
          <div className="border-t border-border px-3 py-2">{removeButton}</div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
